import UploadIcon from 'assets/icons/upload.svg';
import RestoreIcon from 'assets/icons/restore.svg';
import ButtonWithIcon from 'components/ButtonWithIcon';
import Button from 'components/Button';
import { scrollToResources } from 'utils/helpers';
import { IEnterDataSection } from 'interfaces/components/enterDataSection';
import { useAppSelector } from 'store';
import { DashboardPdf } from 'components/dashboardPdf';
import { useAuth } from 'context/AuthContext';

const EnterDataSection = ({
  onComparePerformance,
  onUpdate,
  isDarkMode
}: IEnterDataSection) => {
  const portCoData = useAppSelector(state => state.portCoData);
  const isEntered = useAppSelector(state => state.companyInfo.isEntered);
  const { isAuthenticated, username, isPortCo } = useAuth();
  const userInitials = username
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(char => char?.[0].toUpperCase());

  const welcomeMessage = () => {
    if (isAuthenticated) {
      return `Welcome to ${username}'s dashboard!`;
    } else if (isEntered) {
      return 'Welcome to your dashboard!';
    } else {
      return (
        <>
          Enter your data to overlay it with Compass software benchmarks
          <span className='text-[16px]'>¹</span>
        </>
      );
    }
  };

  return (
    <div className='mx-20 my-6 flex justify-between'>
      <div className='flex gap-4 items-center'>
        {isAuthenticated && (
          <div className='flex items-center justify-center w-[94px] h-[94px] rounded-full border border-gray-400 bg-green-400'>
            <span className='text-[56px] font-semibold text-white dark:text-black'>
              {userInitials}
            </span>
          </div>
        )}
        <div className='flex flex-col gap-2 items-start'>
          <p className='text-[24px] font-light'>{welcomeMessage()}</p>
          <p className='text-base font-light text-gray-700 dark:text-gray-500'>
            {isEntered || isAuthenticated
              ? "Measure your company's results with key benchmark performance metrics."
              : 'By entering your data you will also be able to download your performance dashboard.'}
          </p>
        </div>
      </div>
      <div className='flex items-center gap-2'>
        <Button
          label='Resources'
          id='resources'
          onClick={scrollToResources}
          className='flex items-center gap-1 rounded-5xl px-5 py-[9px] bg-transparent border border-solid border-gray-400 dark:border-gray-850 leading-[19.2px] font-light text-[18px] hover:bg-[#504f4f1b] transition-all duration-300 walkthrough-resources'
        />
        {isEntered && !isPortCo ? (
          <ButtonWithIcon
            id='update_data_button'
            prefixIcon={RestoreIcon}
            prefixIconAlt='Update Icon'
            label='Update Data'
            className='text-green-700 whitespace-nowrap px-3 flex items-center cursor-pointer'
            textStyle='text-[18px] leading-[19.2px] font-light'
            onClick={onUpdate}
          />
        ) : (
          <ButtonWithIcon
            id='enter_your_data_button'
            prefixIcon={UploadIcon}
            prefixIconAlt='Upload Icon'
            label={isAuthenticated ? 'Access your Data' : 'Enter your Data'}
            className='bg-green-500 text-[16px] dark:text-black whitespace-nowrap rounded-5xl px-5 py-[10px] flex items-center justify-center cursor-pointer hover:bg-[#62c77d] transition-all duration-300 walkthrough-compare-performance'
            textStyle='text-[18px] leading-[19.2px] font-light'
            onClick={onComparePerformance}
          />
        )}
        {(isEntered || (isPortCo && portCoData.isDataReviewed)) && (
          <DashboardPdf />
        )}
      </div>
    </div>
  );
};

export default EnterDataSection;
